
import type { IOrderQuickFormatRowSnapshot } from "@/lib/InterfacesAndTypes"
import { buildQuickFormatOutput, type QuickFormatMode } from "@/lib/QuickFormatText"
import LoadingIndicator from "./LoadingIndicator.vue"
import { mapActions, mapState, mapWritableState } from "pinia"
import { isOrderFabRoute, useStore } from "../store/PiniaStore"
import { defineComponent } from "vue"
import { VueRouterInst } from '../InitRoutesPlugins'

function escapeHtmlForQuickFormatClipboard(s: string): string {
	return s
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
}

const QUICK_FORMAT_MODE_STORAGE_KEY = "QuickFormatMode"

function readQuickFormatModeFromStorage(): QuickFormatMode {
	try {
		const raw = localStorage.getItem(QUICK_FORMAT_MODE_STORAGE_KEY)
		if (raw === "email" || raw === "fax" || raw === "plain") {
			return raw
		}
	} catch {
		// ignore SecurityError / unavailable storage
	}
	return "email"
}

export default defineComponent({
	name: 'MainUI',
	components: { LoadingIndicator },
	data: () => {
		const d: {
			isWhatsNewModalActive: boolean
			quickFormatMode: QuickFormatMode
		} = {
			isWhatsNewModalActive: false,
			quickFormatMode: readQuickFormatModeFromStorage(),
		}
		return d
	},
	computed: {
		...mapWritableState(useStore, ["quickFormatModalActive"]),
		...mapState(useStore, ['searchesActive', 'currentUser', 'isLoggedIn', 'FABActionType', 'FABSelectedReportRows', 'FABSelectedReportRowCount', 'FABSelectedQuickFormatRowCount', 'FABSelectedQuickFormatRows', 'FABShowCancel', 'quickFormatMarkAllSentLoading']),
		quickFormatBody(): string {
			const rows = this.FABSelectedQuickFormatRows as IOrderQuickFormatRowSnapshot[]
			return buildQuickFormatOutput(rows, this.quickFormatMode)
		},
		fabClearVisible(): boolean {
			if (this.FABShowCancel) {
				return true
			}
			return isOrderFabRoute(this.$route.path) && this.FABSelectedQuickFormatRowCount > 0
		},
		fabPrimaryVisible(): boolean {
			const path = this.$route.path
			if (isOrderFabRoute(path)) {
				return this.FABSelectedQuickFormatRowCount > 0
			}
			return (this.FABActionType === "generate-report" && this.FABSelectedReportRowCount > 0) || this.FABShowCancel
		},
		fabPrimaryLabel(): string {
			if (this.FABShowCancel) {
				return "Download Report"
			}
			if (isOrderFabRoute(this.$route.path) && this.FABSelectedQuickFormatRowCount > 0) {
				return "Quick format"
			}
			if (this.FABActionType === "generate-report") {
				return "Generate Report"
			}
			return ""
		},
		fabSubtitle(): string {
			if (isOrderFabRoute(this.$route.path) && this.FABSelectedQuickFormatRowCount > 0) {
				return `${this.FABSelectedQuickFormatRowCount} selected rows`
			}
			if (this.FABSelectedReportRowCount > 0) {
				return `${this.FABSelectedReportRowCount} selected cases`
			}
			return ""
		},
	},
	methods: {
		...mapActions(useStore, ['addEmptySearch', 'submitLogout', 'updateColumnPresetSelected', 'doFABAction', 'clearFABAction', 'closeQuickFormatModal', 'markAllQuickFormatRowsSentThenClearFab']),
		async onCloseQuickFormatMarkAllSent() {
			try {
				await this.markAllQuickFormatRowsSentThenClearFab()
				this.$buefy.toast.open({
					duration: 5000,
					message: "Marked all rows as 'court sent'.",
					type: "is-success",
				})
			} catch (err) {
				const message = err instanceof Error ? err.message : "Could not mark all rows sent."
				this.$buefy.toast.open({
					duration: 5000,
					message,
					type: "is-danger",
				})
			}
		},
		async onQuickFormatTextareaInteract(ev: Event) {
			const ta = ev.target as HTMLTextAreaElement | null
			if (!ta) {
				return
			}
			ta.select()
			const text = ta.value
			try {
				if (this.quickFormatMode === "plain") {
					await navigator.clipboard.writeText(text)
				} else if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
					const html = `<pre style="font-family:monospace;white-space:pre-wrap;word-break:break-all">${escapeHtmlForQuickFormatClipboard(text)}</pre>`
					await navigator.clipboard.write([
						new ClipboardItem({
							"text/plain": new Blob([text], { type: "text/plain" }),
							"text/html": new Blob([html], { type: "text/html" }),
						}),
					])
				} else {
					await navigator.clipboard.writeText(text)
				}
				this.$buefy.toast.open({
					duration: 3000,
					message: "Copied quick format to clipboard!",
					type: "is-success",
				})
			} catch {
				this.$buefy.toast.open({
					duration: 4000,
					message: "Could not copy to clipboard.",
					type: "is-danger",
				})
			}
		},
	},
	watch: {
		quickFormatMode(mode: QuickFormatMode) {
			localStorage.setItem(QUICK_FORMAT_MODE_STORAGE_KEY, mode)
		},
	},
	mounted(): void {
		if (!this.isLoggedIn) {
			if (VueRouterInst.currentRoute.path !== '/login') {
				console.log('Not logged in, redirecting to /login')
				VueRouterInst.push({ path: '/login', replace: true })
			}
		}
		this.updateColumnPresetSelected('important')
	},

})
